import "../index.css";
import { useState, useEffect, useCallback, useRef } from "react";
import Footer from "../components/Footer";
import { PageNav } from "../components/PageNav";
import ApiCall from "../auth/ApiCall";
import { useNavigate } from "react-router-dom";
import { RuleLogicDisplay } from "../components/RuleLogic";
import { getRuleOptionSelection } from "../components/ruleOptionSelectors";
import { RuleOptionSummary } from "../components/RuleOptionSelect";
import { findRuleDescription } from "../components/ruleDescriptions";
import buttonBackground from "../assets/button_background_blue.webp";
import buttonBackgroundRed from "../assets/button_background_red.webp";
import { AccessVerification } from "../auth/UserProfile";

interface Rule {
  id: number;
  name: string;
  sport: number;
  sport_name: string;
  enabled: boolean;
  rule_type_id?: number;
  rule_subtype: number | null;
  rule_details: {
    type: string;
    details: Record<string, unknown>;
  };
  house_names: string[];
  teams_display: string[];
  notifications: {
    browser: boolean;
    mobile: boolean;
    text: boolean;
  };
}

// ── Subtype display framework ────────────────────────────────────────────────
// rule_subtype_id semantics:
//   1 / 4 → Value Change
//   2 / 5 → Percent Change
//   3 / 6 → Hits Value
//
// Each entry receives the raw rule_details.details so the description function
// can reference thresholds, directions, etc. once the user fills in the text.

function resolveTemplate(
  template: string,
  values: Record<string, unknown>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const val = values[key];
    return val !== undefined && val !== null ? String(val) : `{${key}}`;
  });
}

const SUBTYPE_DISPLAY: Record<number, { label: string; template: string }> = {
  1: {
    label: "Value Change",
    template:
      "When the {rule_group_description} {rule_definition_description} changes by {value_change} from the Start of the Game",
  },
  2: {
    label: "Percent Change",
    template:
      "When the {rule_group_description} {rule_definition_description} changes by {percent}% from the Start of the Game",
  },
  3: {
    label: "Hits Value",
    template:
      "If the {rule_group_description} {rule_definition_description} is {equality} {value}",
  },
  4: {
    label: "Value Change",
    template: "[Subtype 4 — Value Change placeholder]",
  },
  5: {
    label: "Percent Change",
    template: "[Subtype 5 — Percent Change placeholder]",
  },
  6: {
    label: "Hits Value",
    template: "[Subtype 6 — Hits Value placeholder]",
  },
};

// Explanatory blurb + resolved threshold sentence for rule types whose own
// template isn't self-explanatory on its own — currently Arbitrage/+EV.
// Rendered ahead of SubtypeRuleDisplay/RuleLogicDisplay so it shows even when
// those return nothing (e.g. an unmatched rule_subtype).
function RuleDescriptionBlurb({
  details,
}: {
  details: Record<string, unknown>;
}) {
  const description = `${(details["rule_group_description"] as string) ?? ""} ${
    (details["rule_definition_description"] as string) ?? ""
  }`;
  const entry = findRuleDescription(description);
  if (!entry) return null;
  const hasThreshold = details["equality"] != null && details["value"] != null;
  return (
    <div style={{ marginBottom: 8 }}>
      {/* No color override — inherits text_green from the wrapping div,
          same as the rule sentence below it. */}
      <p
        className="font_family_base"
        style={{ fontSize: 15, lineHeight: 1.5, margin: 0 }}
      >
        {entry.blurb}
      </p>
      {hasThreshold && (
        <p
          className="font_family_base"
          style={{ fontSize: 15, margin: "6px 0 0", lineHeight: 1.5 }}
        >
          {resolveTemplate(entry.template, details)}
        </p>
      )}
    </div>
  );
}

function SubtypeRuleDisplay({ rule }: { rule: Rule }) {
  const details = rule.rule_details.details;
  const subtypeId = rule.rule_subtype as number;
  const entry = SUBTYPE_DISPLAY[subtypeId];
  if (!entry) return null;
  const text = resolveTemplate(entry.template, { ...details });
  const optionSelection = getRuleOptionSelection(details);
  return (
    <div>
      <p
        className="font_family_base"
        style={{ fontSize: 15, margin: 0, lineHeight: 1.5 }}
      >
        {text}
      </p>
      {optionSelection && (
        <RuleOptionSummary
          selector={optionSelection.selector}
          selected={optionSelection.selected}
        />
      )}
    </div>
  );
}

export default function Rules() {
  const [rulesError, setRulesError] = useState<string | null>(null);
  // const [refreshing, setRefreshing] = useState(false);
  const [isRulesLoading, setIsRulesLoading] = useState(true);
  const [sportsMetadata, setSportsMetadata] = useState<Record<string, unknown>>(
    {},
  );
  const [rules, setRules] = useState<Rule[]>([]);
  const [menuVisible, setMenuVisible] = useState(false);
  const [currentRuleId, setCurrentRuleId] = useState<number>(-1);
  const [currentRule, setCurrentRule] = useState<Rule | null>(null);
  const [hasNoRules, setHasNoRules] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentRulePeriodType, setCurrentRulePeriodType] =
    useState<string>("");
  const navigate = useNavigate();
  const hasSelectedInitialRule = useRef(false);
  const lastRefreshedRef = useRef<Date | null>(null);

  const isBasicPlan = (localStorage.getItem("subscription_name") || "")
    .toLowerCase()
    .includes("basic");
  const isAtLimit = isBasicPlan && rules.length >= 5;
  const isOverLimit = isBasicPlan && rules.length > 5;

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Verify user has an active subscription
  useEffect(() => {
    const redirectPath = AccessVerification();
    if (redirectPath) {
      navigate(redirectPath);
    }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const response =
        await ApiCall.makeAuthenticatedRequest("/api/load_rules/");

      if ("error" in response) {
        throw new Error("Network response was not ok");
      } else {
        const data = response as Record<string, unknown>;
        const rulesData = data["rules"] as Rule[];
        setRules(rulesData);
        localStorage.setItem("rule_count", String(rulesData.length));
        if (rulesData.length === 0) {
          setHasNoRules(true);
        } else if (!hasSelectedInitialRule.current && rulesData[0]) {
          hasSelectedInitialRule.current = true;
          setCurrentRule(rulesData[0]);
          setCurrentRulePeriodType(
            (
              (data["sportsMetadata"] as Record<string, unknown>)[
                rulesData[0]["sport"]
              ] as Record<string, unknown>
            )["part_name"] as string,
          );
          setCurrentRuleId(rulesData[0].id);
          setIsEnabled(rulesData[0].enabled);
        }
        setSportsMetadata(data["sportsMetadata"] as Record<string, unknown>);
      }
      lastRefreshedRef.current = new Date();
      setRulesError(null);
    } catch (err: unknown) {
      if (lastRefreshedRef.current === null) {
        setRulesError(
          "Failed to fetch data: " +
            (err instanceof Error ? err.message : "Unknown error"),
        );
      }
    } finally {
      setIsRulesLoading(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Set up the auto-refresh interval
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [fetchData]);

  const toggleRulesList = (): void => {
    setMenuVisible(!menuVisible);
  };

  const closeMenu = (): void => {
    setMenuVisible(false);
  };

  const navigateToRule = (ruleIndex: number): void => {
    setMenuVisible(false);
    if (rules[ruleIndex]) {
      setCurrentRuleId(rules[ruleIndex].id);
      setCurrentRule(rules[ruleIndex]);
      setCurrentRulePeriodType(
        (sportsMetadata[rules[ruleIndex]["sport"]] as Record<string, unknown>)[
          "part_name"
        ] as string,
      );
      setIsEnabled(rules[ruleIndex].enabled);
    }
  };

  const getIconAndColor = (sport: number): string => {
    const sportIcons: Record<number, string> = {
      1: "🏀 ", // NBA
      2: "🏀 ", // NCAAB
      3: "🏒 ", // NHL
      4: "⚾️ ", // MLB
      5: "🏈 ", // NFL
      6: "🏈 ", // NCAAF
      7: "⚽️ ", // MLS
      8: "⚽️ ", // World Cup
    };
    return sportIcons[sport] || "";
  };

  const handleEdit = (id: number): void => {
    navigate(`/rule/edit/${id}`);
  };

  const handleDisable = async (): Promise<void> => {
    await ApiCall.makeAuthenticatedRequest("/api/enable_rule/", {
      method: "PUT",
      body: JSON.stringify({ ruleId: currentRuleId, enabled: !isEnabled }),
    });
    setIsEnabled(!isEnabled);
  };

  const handleDeleteClick = (): void => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    setShowDeleteConfirm(false);
    const ruleIdToDelete = currentRuleId;
    setCurrentRuleId(-1);
    setCurrentRule(null);
    hasSelectedInitialRule.current = false;
    await ApiCall.makeAuthenticatedRequest("/api/delete_rule/", {
      method: "PUT",
      body: JSON.stringify({ ruleId: ruleIdToDelete }),
    });
    fetchData();
  };

  const handleDeleteCancel = (): void => {
    setShowDeleteConfirm(false);
  };

  if (hasNoRules) {
    return (
      <div>
        <PageNav />
        <div className="show-desktop">
          <div className="main_div">
            <div className="center_me" style={{ padding: 40 }}>
              <p className="font_family_base" style={{ fontSize: 18 }}>
                No rules defined yet. Go to "Create New Rule" to make your first
                one.
              </p>
            </div>
          </div>
        </div>
        <div className="show-mobile">
          <div className="main_div">
            <div className="center_me row" style={{ padding: 40 }}>
              <p className="font_family_base" style={{ fontSize: 18 }}>
                No rules defined yet.
              </p>
              <p className="font_family_base" style={{ fontSize: 18 }}>
                Go to Create Rule to make your first one.
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (isRulesLoading || currentRuleId < 0 || !currentRule) {
    return (
      <div>
        <PageNav />
        <div className="main_div">
          <div className="center_me" style={{ padding: 40 }}>
            <p className="font_family_base">Loading...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (rulesError) {
    return (
      <div>
        <PageNav />
        <div className="main_div">
          <div className="center_me" style={{ padding: 40 }}>
            <span>⛔️</span> {rulesError}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <PageNav />
      <div className="main_div">
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            paddingRight: "10px",
            paddingLeft: "10px",
            overflow: "hidden",
            width: "100%",
          }}
        >
          {/* Basic plan rule limit banner */}
          {isOverLimit && (
            <div
              className="font_family_base"
              style={{
                backgroundColor: "#2a0000",
                border: "1px solid #ef4444",
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: 16,
                color: "#ef4444",
                textAlign: "center",
                fontSize: 14,
              }}
            >
              Your Basic plan allows up to 5 rules. Please delete{" "}
              {rules.length - 5} rule{rules.length - 5 > 1 ? "s" : ""} to regain
              access to other pages.
            </div>
          )}
          {!isOverLimit && isAtLimit && (
            <div
              className="font_family_base"
              style={{
                backgroundColor: "#2a1a00",
                border: "1px solid #f59e0b",
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: 16,
                color: "#f59e0b",
                textAlign: "center",
                fontSize: 14,
              }}
            >
              You've reached the 5-rule limit for the Basic plan. Upgrade to Pro
              for unlimited rules.
            </div>
          )}

          {/* Header Row */}
          <div
            className="center_me"
            style={{
              display: "flex",
              gap: 10,
              marginBottom: 20,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={toggleRulesList}
              className="font_family_base "
              style={{
                padding: "15px 20px",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                flex: "0 0 auto",
                background: "transparent",
              }}
            >
              <span className="font_family_base" style={{ fontSize: 16 }}>
                {getIconAndColor(currentRule.sport)} ({currentRule.sport_name}){" "}
                {currentRule.name}
              </span>
              <span style={{ marginLeft: "10px" }}>▼</span>
            </button>

            {/* <div
                  className="font_family_base center_me  border_radius"
                  style={{
                    // backgroundColor: "#181818",
                    // borderRadius: 8,
                    padding: "15px 20px",
                    color: "#fff",
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span className="font_family_base" style={{ fontSize: 16 }}>
                    {getIconAndColor(currentRule.sport)} {currentRule.name}
                  </span>
                  {!isEnabled && (
                    <span
                      className="font_family_base"
                      style={{
                        backgroundColor: "#fa5252",
                        padding: "4px 12px",
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: "bold",
                      }}
                    >
                      DISABLED
                    </span>
                  )}
                </div> */}
          </div>
          <div className="center_me">
            {!isEnabled && (
              <span
                className="font_family_base"
                style={{
                  backgroundColor: "#FF0000",
                  marginLeft: "10px",
                  padding: "4px 12px",
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: "bold",
                }}
              >
                DISABLED
              </span>
            )}
          </div>

          {/* Rule description */}
          <div className="border_radius" style={{ padding: 20 }}>
            <div
              className="font_family_base text_green center_me"
              style={{
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              <RuleDescriptionBlurb
                details={currentRule.rule_details.details}
              />
              {currentRule.rule_subtype != null ? (
                <SubtypeRuleDisplay rule={currentRule} />
              ) : (
                <RuleLogicDisplay
                  gamePartCount={0}
                  gamePartName={currentRulePeriodType}
                  ruleDetails={currentRule["rule_details"]["details"]}
                  ruleType={currentRule["rule_details"]["type"]}
                  textColor="#00bf62"
                />
              )}
            </div>
          </div>

          {/* Three Column Layout */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: 20,
              marginBottom: 20,
            }}
          >
            {/* Sportsbooks */}
            <div
              className=" border_radius"
              style={{
                // backgroundColor: "#181818",
                // borderRadius: 8,
                padding: 20,
              }}
            >
              <h4
                className="font_family_base center_me"
                style={{ marginBottom: 15 }}
              >
                Sportsbooks
              </h4>
              {currentRule.house_names.map((book, index) => (
                <div
                  key={index}
                  className="font_family_base background_3 border_radius has_border"
                  style={{
                    // backgroundColor: "#4CAF50",
                    padding: 10,
                    marginBottom: 10,
                    // borderRadius: 4,
                    textAlign: "center",
                  }}
                >
                  {book}
                </div>
              ))}
            </div>

            {/* Teams */}
            <div
              className=" border_radius"
              style={{
                // backgroundColor: "#181818",
                // borderRadius: 8,
                padding: 20,
              }}
            >
              <h4
                className="font_family_base center_me"
                style={{ marginBottom: 15 }}
              >
                {currentRule.rule_type_id === 5 ? "Players" : "Teams"}
              </h4>
              {currentRule.teams_display.map((team, index) => (
                <div
                  key={index}
                  className="font_family_base background_3 border_radius has_border"
                  style={{
                    // backgroundColor: "#4CAF50",
                    padding: 10,
                    marginBottom: 10,
                    // borderRadius: 4,
                    textAlign: "center",
                  }}
                >
                  {team}
                </div>
              ))}
            </div>

            {/* Notifications */}
            <div
              className=" border_radius"
              style={{
                // backgroundColor: "#181818",
                // borderRadius: 8,
                padding: 20,
              }}
            >
              <h4
                className="font_family_base center_me"
                style={{ marginBottom: 15 }}
              >
                Notifications
              </h4>
              <div
                className={`font_family_base border_radius ${
                  currentRule.notifications.mobile
                    ? "background_3 has_border"
                    : "background_2"
                }`}
                style={{
                  padding: 10,
                  marginBottom: 10,
                  textAlign: "center",
                }}
              >
                Mobile Notifications:{" "}
                {currentRule.notifications.mobile ? "ON" : "OFF"}
              </div>
              {/* {currentRule.notifications.text && (
                <div
                  className="font_family_base background_green border_radius"
                  style={{
                    // backgroundColor: "#4CAF50",
                    padding: 10,
                    marginBottom: 10,
                    // borderRadius: 4,
                    textAlign: "center",
                  }}
                >
                  Text
                </div>
              )} */}
            </div>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: 20,
              marginBottom: 20,
            }}
          >
            <div className="center_me">
              <button
                onClick={() => handleEdit(currentRuleId)}
                //                className="my-form-button-wide font_family_base button_text button_color"
                className="my-form-button-wide button-base button-3d font_family_base"
                style={{
                  marginTop: "5px",
                  marginBottom: "5px",
                  backgroundImage: `url(${buttonBackground})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  color: "white",
                  fontWeight: 700,
                }}
              >
                Edit
              </button>
            </div>
            <div className="center_me">
              <button
                onClick={handleDisable}
                // className="my-form-button-wide font_family_base button_text button_color"
                className="my-form-button-wide button-base button-3d font_family_base"
                style={{
                  marginTop: "5px",
                  marginBottom: "5px",
                  backgroundImage: `url(${buttonBackground})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  color: "white",
                  fontWeight: 700,
                }}
              >
                {isEnabled ? "Disable" : "Enable"}
              </button>
            </div>
            <div className="center_me">
              <button
                onClick={handleDeleteClick}
                className="my-form-button-wide button-base button-3d-red font_family_base"
                style={{
                  marginTop: "5px",
                  marginBottom: "5px",
                  backgroundImage: `url(${buttonBackgroundRed})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  color: "white",
                  fontWeight: 700,
                }}
              >
                Delete
              </button>
            </div>
          </div>

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <>
              <div
                onClick={handleDeleteCancel}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  zIndex: 1000,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="background_2 border_radius"
                  style={{
                    padding: 30,
                    maxWidth: 400,
                    width: "90%",
                    textAlign: "center",
                    border: "1px solid #333",
                  }}
                >
                  <h3
                    className="font_family_base"
                    style={{
                      color: "#fff",
                      marginBottom: 15,
                      fontSize: 20,
                    }}
                  >
                    Delete Rule
                  </h3>
                  <p
                    className="font_family_base"
                    style={{
                      color: "#ccc",
                      marginBottom: 25,
                      fontSize: 16,
                      lineHeight: 1.5,
                    }}
                  >
                    Are you sure you want to delete "{currentRule?.name}"? This
                    action cannot be undone.
                  </p>
                  <div
                    style={{
                      display: "flex",
                      gap: 15,
                      justifyContent: "center",
                    }}
                  >
                    <button
                      onClick={handleDeleteCancel}
                      className="my-form-button-wide button-base button-3d font_family_base"
                      style={{
                        backgroundImage: `url(${buttonBackground})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        color: "white",
                        fontWeight: 700,
                        padding: "12px 24px",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteConfirm}
                      className="my-form-button-wide button-base button-3d-red font_family_base"
                      style={{
                        backgroundImage: `url(${buttonBackgroundRed})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        color: "white",
                        fontWeight: 700,
                        padding: "12px 24px",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* AI Analytics */}
          {/* <div
            className="background_1 border_radius"
            style={{
              padding: 20,
              textAlign: "center",
            }}
          >
            <h4 className="font_family_base" style={{ marginBottom: 15 }}>
              AI Analytics
            </h4>
            <div
              className="font_family_base background_2 border_radius"
              style={{
                padding: 30,
              }}
            >
              COMING SOON
            </div>
          </div> */}
        </div>

        {/* Side Menu Overlay */}
        {menuVisible && (
          <>
            <div
              onClick={closeMenu}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 999,
              }}
            />
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                bottom: 0,
                width: "min(80%, 300px)",
                backgroundColor: "#000",
                borderRight: "1px solid #333",
                zIndex: 1000,
                overflowY: "auto",
                padding: "20px",
              }}
            >
              <h2
                className="font_family_base"
                style={{
                  color: "#4CAF50",
                  marginBottom: 20,
                  textAlign: "center",
                }}
              >
                MY RULES
              </h2>
              {rules.map((rule, index) => (
                <button
                  key={rule.id}
                  onClick={() => navigateToRule(index)}
                  className="font_family_base"
                  style={{
                    width: "100%",
                    padding: "15px",
                    marginBottom: "10px",
                    backgroundColor:
                      currentRuleId === rule.id ? "#007bff" : "#181818",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {getIconAndColor(rule.sport)} ({rule.sport_name}) {rule.name}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
